import { useState } from 'react';
import styled from 'styled-components';

import { Splide, SplideTrack, SplideSlide } from '@splidejs/react-splide';
import Button from 'components/ui-components/Button';

import { setGtmClickAttr } from 'lib/utils';
import { ZoomIcon } from 'assets/icons';
import useIsSsr from 'hooks/useIsSSR';
import { grey50 } from 'constants/variables';

import ImageZoom from './image_zoom';

import Arrow from '../../../../assets/images/shared/icons/arrow-right-grey.svg';
import YoutubeIcon from '../../../../assets/images/shared/icons/youtube.svg';

/**
 * @param {Object} props
 * @param {Object} props.media - media assets to populate the main product carousel
 * @returns the product image carousel
 */
const ProductCarousel = ({ media, alt }) => {
  const { images, zoom, thumbs, large_thumbs: largeThumbs, video } = media;

  const [currentSlide, setCurrentSlide] = useState(0);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const isSsr = useIsSsr();

  /**
   * Closing the zoom modal
   */
  const closeZoom = () => {
    setModalIsOpen(false);
    document.querySelector(`.splide__slide--${currentSlide} button`).focus();
  };

  const handleZoom = () => {
    setModalIsOpen(true);
  };

  /**
   * method to pause a youtube video
   * TODO: move into utils if we need to use this elsewhere
   */
  const pauseVideo = () => {
    const youtubeFrame = document.querySelector('.splide__youtube');
    youtubeFrame.contentWindow.postMessage(
      '{"event":"command","func":"stopVideo","args":""}',
      '*',
    );
  };

  const handlePaginationMount = (_instance, data) => {
    data.list.classList.add('splide__pagination--thumbnails');

    // `items` contains all dot items
    // create product image thumbnails
    for (let i = 0; i < data.items.length; i++) {
      if (i === thumbs.length) {
        break;
      }

      const img = document.createElement('img');

      img.src = thumbs[i];
      img.srcset = `${thumbs[i]}, ${largeThumbs[i]} 2x`;
      img.alt = alt;

      // set first thumbnail to active state
      if (i === 0) {
        data.items[i].button.classList.add('is-active');
      }

      // add to DOM
      data.items[i].button.appendChild(img);
      setGtmClickAttr(data.items[i].button);
    }

    // create youtube thumbnail
    if (video) {
      const img = document.createElement('img');

      img.src = YoutubeIcon;
      img.alt = '';
      img.classList.add('splide__youtube-icon');

      data.items[data.items.length - 1].button.appendChild(img);
      setGtmClickAttr(data.items[data.items.length - 1].button);
    }
  };

  /**
   * Sets the current slide in state and pauses youtube video when moving away from it
   * @param {number} current - the current carousel slide
   * @param {number} prev - the previous carousel slide
   */
  const handleSlideMove = (_instance, current, prev) => {
    setCurrentSlide(current);

    // pause youtube video when moving away from it
    // ideally we would only run this if we can somehow detect youtube vid is playing
    if (prev === images.length && current !== images.length) {
      pauseVideo();
    }
  };

  if (!images.length) return null;

  if ((images.length === 1 && !video) || isSsr) {
    return (
      <>
        <div className="splide__slide--0">
          <StyledSlideSingular
            handleClick={handleZoom}
            label="click to zoom"
            className="splide__slide--singular">
            <picture>
              <source
                srcSet={images[0].replace('.jpg', '.webp')}
                type="image/webp"
              />
              <img
                src={images[0]}
                alt={alt || ''}
                width="370"
                height="370"
                className="product-carousel__image"
                fetchPriority="high"
              />
            </picture>
          </StyledSlideSingular>
        </div>

        <ZoomButton
          handleClick={handleZoom}
          theme={{ show: currentSlide < images.length }}
          className="tc-grey-60 fs-12 mtb-12">
          <ZoomIcon fillColor={grey50} className="mr-4" />
          Click to Zoom
        </ZoomButton>

        {currentSlide !== zoom.length && (
          <ImageZoom
            image={zoom[currentSlide]}
            modalIsOpen={modalIsOpen}
            closeModal={closeZoom}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Splide
        hasTrack={false}
        aria-label="Product image carousel"
        onPaginationMounted={handlePaginationMount}
        onMove={handleSlideMove}
        options={{ rewind: true }}>
        <div className="splide__arrows">
          <Button className="splide__arrow splide__arrow--prev">
            <img alt="previous" src={Arrow} width="20" height="30" />
          </Button>
          <Button className="splide__arrow splide__arrow--next">
            <img alt="next" src={Arrow} width="20" height="30" />
          </Button>
        </div>

        <SplideTrack>
          {images.map((img, i) => (
            <StyledSlide
              className={`splide__slide splide__slide--${i}`}
              key={img}>
              <Button handleClick={handleZoom} label={`image ${i + 1}`}>
                <picture>
                  <source
                    srcSet={img.replace('.jpg', '.webp')}
                    type="image/webp"
                  />
                  <img
                    src={img}
                    alt={alt || ''}
                    width="384"
                    height="384"
                    srcSet={`${img}, ${zoom[i]} 2x`}
                    className="product-carousel__image"
                    fetchPriority="high"
                  />
                </picture>
              </Button>
            </StyledSlide>
          ))}

          {video && (
            <StyledSlide className="splide__slide fl fl-col jc-c">
              <iframe
                title="youtube"
                src={`${video}?enablejsapi=1`}
                className="splide__youtube"
                allow="fullscreen"
              />
            </StyledSlide>
          )}
        </SplideTrack>

        <ZoomButton
          handleClick={handleZoom}
          theme={{ show: currentSlide < images.length }}
          className="tc-grey-60 fs-12 mtb-12">
          <ZoomIcon fillColor={grey50} className="mr-4" />
          Click to Zoom
        </ZoomButton>
      </Splide>
      {currentSlide !== zoom.length && (
        <ImageZoom
          image={zoom[currentSlide]}
          modalIsOpen={modalIsOpen}
          closeModal={closeZoom}
        />
      )}
    </>
  );
};

const StyledSlide = styled(SplideSlide)`
  display: flex;

  button {
    padding: 0;
    border: 0;
    background-color: transparent;
    width: 80%;
    margin: 0 auto;
  }

  img {
    width: 100%;
  }
`;

const StyledSlideSingular = styled(Button)`
  padding: 0;
  border: 0;
  background-color: transparent;
  width: 100%;
`;

const ZoomButton = styled(Button)`
  background-color: transparent;
  width: auto;
  visibility: ${({ theme }) => (theme?.show ? 'visible' : 'hidden')};
`;

export default ProductCarousel;
